import type AirDatepicker from 'air-datepicker'

/**
 * AirDatepicker 3.6 has no auto-flip at all. Its `setPosition()` reads only the
 * field's `getBoundingClientRect()` and the configured side — it never looks at
 * `window.innerHeight`. With the default `'bottom left'` a field near the bottom
 * of a tall form opens its calendar past the viewport: the user sees the top
 * edge and can click nothing.
 *
 * Two approaches were tried and rejected before this one:
 *
 *  - `scrollIntoView` after opening — works, but yanks the page. The user
 *    expects the calendar to open upwards, not the screen to move.
 *  - the library's `position` *callback* — the calculation was right, but taking
 *    positioning over means also taking over teardown (`customHide`), and the
 *    lifecycle broke with `Cannot read properties of null (reading 'removeChild')`.
 *
 * So we keep the library in charge: we only decide *which side* to ask for and
 * hand it back the plain string form, which `setPosition()` supports natively.
 *
 * @see flipDatepickerIfClipped for the timing that makes this flicker-free.
 */

/** Library default (`opts.offset`) — the gap between field and popup. */
const DEFAULT_OFFSET = 12

/**
 * Position as originally configured, per instance.
 *
 * Deciding from the *current* value would make the flip sticky: once flipped to
 * `top`, a picker would stay on top even after the user scrolls and there is
 * room below again. We always decide from the original.
 */
const originalPositions = new WeakMap<object, string>()

export interface DatepickerPositionInput {
    /** Configured position, e.g. `'bottom left'`. */
    position: string
    /** Field top edge, viewport coordinates. */
    fieldTop: number
    fieldHeight: number
    /** Rendered popup height; `0` means "not measured yet". */
    popupHeight: number
    viewportHeight: number
    offset: number
}

/**
 * Which side the calendar should open to.
 *
 * Pure on purpose: the arithmetic is the part worth testing, and it needs no DOM.
 *
 * Rules, in order:
 *  1. anything we cannot measure — leave the configured value alone;
 *  2. a position whose primary side is horizontal (`'left top'`) is not ours to
 *     flip — we only ever swap top↔bottom;
 *  3. the configured side wins whenever the popup fits there;
 *  4. otherwise flip, if the popup fits on the other side;
 *  5. if it fits nowhere, pick the roomier side — better most of a calendar than
 *     the top edge of one.
 */
export const resolveDatepickerPosition = ({
    position,
    fieldTop,
    fieldHeight,
    popupHeight,
    viewportHeight,
    offset,
}: DatepickerPositionInput): string => {
    const [side, align] = position.split(' ')

    if (side !== 'top' && side !== 'bottom') {
        return position
    }

    if (popupHeight <= 0 || viewportHeight <= 0) {
        return position
    }

    const spaceBelow = viewportHeight - (fieldTop + fieldHeight) - offset
    const spaceAbove = fieldTop - offset

    const fits = { bottom: spaceBelow >= popupHeight, top: spaceAbove >= popupHeight }

    let next: 'top' | 'bottom'

    if (fits[side]) {
        next = side
    } else {
        const other = side === 'bottom' ? 'top' : 'bottom'

        next = fits[other] ? other : spaceBelow >= spaceAbove ? 'bottom' : 'top'
    }

    return next === side ? position : `${next}${align ? ` ${align}` : ''}`
}

/**
 * Swaps the classes the library set once, at creation.
 *
 * `_setPositionClasses()` runs a single time in `_createComponents()` and only
 * ever *adds* — so after a flip the arrow would still point down
 * (`-bottom-left-`) and the entrance transform would still come from below
 * (`-from-bottom-`). `setPosition()` writes `style.cssText` and touches neither.
 */
const swapPositionClasses = (popup: HTMLElement, from: string, to: string): void => {
    const [fromSide, fromAlign] = from.split(' ')
    const [toSide, toAlign] = to.split(' ')

    if (fromAlign) popup.classList.remove(`-${fromSide}-${fromAlign}-`)
    popup.classList.remove(`-from-${fromSide}-`)

    if (toAlign) popup.classList.add(`-${toSide}-${toAlign}-`)
    popup.classList.add(`-from-${toSide}-`)
}

/** The bits of the instance we need that the shipped typings don't declare. */
interface DatepickerInternals {
    opts?: { position?: unknown; offset?: number }
    setPosition?: (position?: string) => void
}

/**
 * Re-opens the calendar on the other side when it would be clipped.
 *
 * **Call this from `onShow(isAnimationComplete === false)`.** That callback runs
 * synchronously inside `show()`, right after the library positioned the popup
 * and added `-active-`, and before the browser paints — so the correction is
 * invisible. Called later (after the transition, or from a timeout) the user
 * would see the calendar jump.
 *
 * Safe to call more than once: with nothing to change it returns early.
 */
export const flipDatepickerIfClipped = (datepicker: AirDatepicker | null | undefined): void => {
    if (!datepicker) return

    const popup = datepicker.$datepicker
    const field = datepicker.$el as unknown as HTMLElement | undefined

    if (!popup || typeof field?.getBoundingClientRect !== 'function') return

    const internals = datepicker as unknown as DatepickerInternals
    const configured = internals.opts?.position

    // A callback position means the host took positioning over — not ours to touch
    if (typeof configured !== 'string' || typeof internals.setPosition !== 'function') return

    const original = originalPositions.get(datepicker) ?? configured
    originalPositions.set(datepicker, original)

    const rect = field.getBoundingClientRect()

    const next = resolveDatepickerPosition({
        position: original,
        fieldTop: rect.top,
        fieldHeight: rect.height,
        popupHeight: popup.offsetHeight,
        viewportHeight: window.innerHeight,
        offset: internals.opts?.offset ?? DEFAULT_OFFSET,
    })

    if (next === configured) return

    // Keep `opts` in step: the library's own resize handler re-positions from it
    if (internals.opts) internals.opts.position = next

    swapPositionClasses(popup, configured, next)
    internals.setPosition(next)
}
